/**
 * A memo's privacy toggle + owner, as one right-aligned cluster of
 * size-6 controls. Lives in the title row on the composer surfaces and at the
 * end of the attribution row on the record slideout — in READ mode as well as
 * inline edit (see MemoAttributionRow), so the cluster's markup exists once
 * and the two placements can't drift apart.
 *
 * The owner half has two skins, picked by `owner.kind`: the interactive
 * picker while editing, and a plain avatar in the same size-6 box while
 * reading (the record slideout's read view has no owner picker, but the
 * avatar must not move when the user clicks in).
 */

import type { Membership } from 'api-client';

import { CrmObjectAvatar } from '@/lib/crm/components/CrmObjectAvatar';
import type { CrmObject } from '@/types/crm';

import { MemoOwnerPicker } from './MemoOwnerPicker';
import { MemoPrivacyToggle } from './MemoPrivacyToggle';
import type { MemoPrivacy } from './notes.utils';

export type MemoFormControlsOwner =
  | {
      /** Editable: the avatar opens the owner picker. */
      kind: 'picker';
      users: Membership[] | undefined;
      ownerMember: Membership | undefined;
      orgId: number | undefined;
      onSelect: (entityId: string) => void;
    }
  | {
      /** Read-only: just the owner's avatar, same box as the picker trigger. */
      kind: 'avatar';
      object: CrmObject | undefined;
    };

export interface MemoFormControlsProps {
  privacy: MemoPrivacy;
  canTogglePrivacy: boolean;
  onPrivacyChange: (isShared: boolean) => void;
  /** Locks both controls while an explicit (non-autosaving) save is in flight. */
  submitLock: boolean;
  owner: MemoFormControlsOwner;
}

export function MemoFormControls({
  privacy,
  canTogglePrivacy,
  onPrivacyChange,
  submitLock,
  owner,
}: MemoFormControlsProps) {
  return (
    <div className='flex shrink-0 items-center gap-1'>
      {/* `flex` on each wrapper (not just `shrink-0`) blockifies the
         inline-flex button inside it. Left inline-level, an inline-flex box
         takes its baseline from its first flex item — an icon contributes
         none, but the owner avatar's initials contribute a real text
         baseline, so the two controls landed on different lines. Blockifying
         opts them out of baseline alignment; the row's items-center is then
         the only thing positioning them. */}
      <div className='shrink-0 flex'>
        <MemoPrivacyToggle
          privacy={privacy}
          onChange={onPrivacyChange}
          isDisabled={!canTogglePrivacy || submitLock}
        />
      </div>
      {owner.kind === 'picker'
        ? owner.users &&
          owner.users.length > 0 && (
            <div className='shrink-0 flex'>
              <MemoOwnerPicker
                users={owner.users}
                ownerMember={owner.ownerMember}
                orgId={owner.orgId}
                onSelect={owner.onSelect}
                isDisabled={submitLock}
              />
            </div>
          )
        : owner.object && (
            <span className='inline-flex size-6 shrink-0 items-center justify-center'>
              <CrmObjectAvatar object={owner.object} size='xxs' />
            </span>
          )}
    </div>
  );
}
